import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, RefreshCw, Wand2, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePinSecurity } from "@/contexts/PinContext";

// V1.33 · Bloque E — Reconciliación documental (caso Pabel)
// Aparece en la ficha del alumno cuando hay documentos huérfanos:
// · student_uploads sin subir a OneDrive
// · documents[] legacy con base64 no migrado
// · corporate_files activos sin onedrive_item_id (subida a Graph falló)

export default function ReconciliationBanner({ studentId, onFixed }) {
  const pin = usePinSecurity();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/reconciliation`);
      setStatus(data);
    } catch {
      // silencioso — no bloqueamos la ficha
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  if (loading || !status?.has_pending) return null;

  const doFix = async () => {
    setFixing(true);
    try {
      await pin.require("corporate.provision");
      const { data } = await api.post(`/students/${studentId}/reconciliation/fix`);
      const legacy = data?.migrated || 0;
      const uploads = data?.alta_uploads?.migrated || 0;
      const errors = (data?.errors || 0) + (data?.alta_uploads?.errors || 0);
      if (errors > 0) {
        toast.warning(`Reconciliación con errores: ${legacy + uploads} archivos reconciliados, ${errors} errores`);
      } else {
        toast.success(`✅ ${legacy + uploads} archivos reconciliados en OneDrive`);
      }
      await load();
      onFixed?.();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      if (!/cancel/i.test(m)) toast.error(m);
    } finally {
      setFixing(false);
    }
  };

  const total = status.total_pending || 0;

  return (
    <div
      className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 space-y-2"
      data-testid="reconciliation-banner"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-amber-900">
            ⚠ {total} documento{total === 1 ? "" : "s"} pendiente{total === 1 ? "" : "s"} de reconciliación
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            Este alumno tiene archivos que existen en MongoDB pero{" "}
            <b>no están en OneDrive corporativo</b>. Reconcilia para restaurar la trazabilidad.
          </div>
          <div className="flex flex-wrap gap-3 text-[11px] mt-1.5 text-amber-800">
            {status.uploads_pending > 0 && (
              <span data-testid="rec-uploads-count">
                📷 {status.uploads_pending} foto{status.uploads_pending === 1 ? "" : "s"} del Alta Digital
              </span>
            )}
            {status.legacy_pending > 0 && (
              <span data-testid="rec-legacy-count">
                📎 {status.legacy_pending} documento{status.legacy_pending === 1 ? "" : "s"} legacy (base64)
              </span>
            )}
            {status.corporate_orphans > 0 && (
              <span data-testid="rec-orphans-count">
                🔗 {status.corporate_orphans} sin ID OneDrive (huérfano)
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={load}
            className="p-1.5 rounded hover:bg-amber-100 text-amber-700"
            title="Refrescar"
            data-testid="rec-refresh"
          >
            <RefreshCw className={"h-3.5 w-3.5 " + (loading ? "animate-spin" : "")} />
          </button>
          <button
            onClick={doFix}
            disabled={fixing}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 disabled:opacity-60"
            data-testid="rec-fix-btn"
          >
            {fixing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
            {fixing ? "Reconciliando…" : "Reconciliar ahora"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Helper: pequeño badge de estado global del sistema
export function ReconciliationGlobalBadge() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get("/corporate/reconciliation/summary")
      .then(({ data }) => { if (!cancel) setSummary(data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  if (!summary?.totals?.total_pending) return null;
  return (
    <div className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5">
      <AlertTriangle className="h-3 w-3" />
      {summary.totals.total_pending} sin reconciliar · {summary.totals.affected_students} alumnos
    </div>
  );
}

// Componente detallado (panel admin) — se puede colocar en Configuración
export function ReconciliationPanel() {
  const pin = usePinSecurity();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fixingSid, setFixingSid] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/corporate/reconciliation/summary");
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fixOne = async (sid) => {
    setFixingSid(sid);
    try {
      await pin.require("corporate.provision");
      const { data } = await api.post(`/students/${sid}/reconciliation/fix`);
      const legacy = data?.migrated || 0;
      const uploads = data?.alta_uploads?.migrated || 0;
      toast.success(`${legacy + uploads} archivos reconciliados`);
      await load();
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      if (!/cancel/i.test(m)) toast.error(m);
    } finally {
      setFixingSid("");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-neutral-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Analizando reconciliación…
      </div>
    );
  }

  const rows = data?.students || [];
  const totals = data?.totals || {};

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4 space-y-3" data-testid="reconciliation-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className={"h-4 w-4 " + (rows.length ? "text-amber-600" : "text-emerald-500")} />
          <h3 className="font-bold text-sm">Reconciliación documental corporativa</h3>
          <span className="text-[10px] font-mono text-neutral-500">V1.33-E</span>
        </div>
        <button onClick={load} className="text-xs text-blue-600 hover:underline">
          <RefreshCw className="h-3 w-3 inline mr-1" /> Refrescar
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg text-sm text-emerald-800" data-testid="rec-all-clean">
          <CheckCircle2 className="h-4 w-4" />
          Todo en orden — cero documentos pendientes de reconciliación.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2 text-center text-xs">
            <StatCard label="Alumnos afectados" value={totals.affected_students} color="amber" />
            <StatCard label="Fotos alta" value={totals.uploads_pending} color="amber" />
            <StatCard label="Legacy base64" value={totals.legacy_pending} color="amber" />
            <StatCard label="Huérfanos" value={totals.corporate_orphans} color="red" />
          </div>
          <div className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {rows.map((r) => (
              <div key={r.student_id} className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-neutral-50">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-neutral-900 truncate">
                    {r.student_name} {r.primer_apellido}
                    {r.codigo_expediente && (
                      <span className="ml-1 text-[10px] font-mono text-neutral-500">
                        [{r.codigo_expediente}]
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-neutral-500 flex gap-2 flex-wrap">
                    {r.uploads_pending > 0 && <span>📷 {r.uploads_pending}</span>}
                    {r.legacy_pending > 0 && <span>📎 {r.legacy_pending}</span>}
                    {r.corporate_orphans > 0 && <span>🔗 {r.corporate_orphans}</span>}
                    <span className="font-bold">Total: {r.total_pending}</span>
                  </div>
                </div>
                <button
                  onClick={() => fixOne(r.student_id)}
                  disabled={fixingSid === r.student_id}
                  className="inline-flex items-center gap-1 h-7 px-2 rounded-full bg-amber-600 text-white text-[10px] font-bold hover:bg-amber-700 disabled:opacity-60"
                  data-testid={`fix-${r.student_id}`}
                >
                  {fixingSid === r.student_id ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Wand2 className="h-3 w-3" />
                  )}
                  Reconciliar
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  const cls =
    color === "red"
      ? "bg-red-50 border-red-200 text-red-800"
      : "bg-amber-50 border-amber-200 text-amber-800";
  return (
    <div className={"rounded-lg border p-2 " + cls}>
      <div className="text-lg font-bold">{value || 0}</div>
      <div className="text-[10px]">{label}</div>
    </div>
  );
}
